import { isNotEmpty, type DecoratedGameParticipant, type GameAward, type GameData } from '@terros/common'
import { useMemo, useState } from 'react'

export type RaffleWinnerAndPrizes = {
  award: GameAward
  winner: DecoratedGameParticipant
}

type Return = {
  nextWinner: RaffleWinnerAndPrizes | undefined
  incrementWinner: () => void
  isFinalWinner: boolean
  winners: RaffleWinnerAndPrizes[]
}

export const useWinningParticipants = (game: GameData, participants: DecoratedGameParticipant[]): Return => {
  const [winnerIndex, setWinnerIndex] = useState<number>(0)

  const { awards } = game

  const winners: RaffleWinnerAndPrizes[] = useMemo(() => {
    return participants
      .flatMap((p) => {
        if ('prizeChoices' in p) {
          const wonAwards = p.prizeChoices?.map((choice) => {
            const award = awards.find((a) => a.place === choice.place)
            if (!award) return
            return { award, winner: p }
          })
          return wonAwards
        }
        if ('prizeIds' in p) {
          const wonAwards = awards.filter((a) =>
            p.prizeIds?.some((id) => a.prizes.some((prize) => prize.prizeId === id))
          )
          return wonAwards.map((award) => ({ award, winner: p }))
        }
      })
      .filter(isNotEmpty)
      .sort((a, b) => a.award.place - b.award.place)
  }, [participants, awards])

  return useMemo(
    () => ({
      nextWinner: winners.find((_, i) => i === winnerIndex),
      incrementWinner: () => setWinnerIndex((prev) => prev + 1),
      isFinalWinner: winnerIndex === winners.length - 1,
      winners,
    }),
    [winnerIndex, winners]
  )
}
